import { clearObject } from "@/modules/utils";
import { useApiWithAuth, DownloadViewReturn } from "@/modules/api";
import { useAuth } from "@/modules/auth";
import {
  ClientePayload,
  useClienteService,
  useClienteLogadoService
} from "@/modules/business/cliente/clienteBusiness";
import {
  TransportadorPayload,
  useTransportadorService
} from "@/modules/business/transportador/transportadorBusiness";
import {
  EngenheiroPayload,
  useEngenheiroService
} from "@/modules/business/engenheiro/engenheiroBusiness";
import {
  useVeiculoTratorService,
  VeiculoTratorPayload
} from "@/modules/business/veiculo/veiculoTratorBusiness";
import { CarretaPayload } from "@/modules/business/veiculo/carretaBusiness";
import {
  VeiculoConjuntoTransportadorGabaritoPayload,
  useGabaritoService
} from "@/modules/business/licenca/gabaritoBusiness";
import { DocumentoPayload } from "@/modules/business/common/commonBusiness";
import { LicencaOrgaoPayload } from "@/modules/business/licenca/licencaOrgaoBusiness";
import { PermissionPayload } from "@/modules/auth";

interface LicencaFilterPayload {
  idCliente?: number;
  idTransportador?: number;
  idCarretas: Array<number>;
  idOrgao?: number;
  idGabarito?: number;
  idVeiculoTrator?: number;
  dtInicial?: Date;
  dtFinal?: Date;
  flSituacoes: Array<string>;
  numeroPedido?: string;
  dtValidadeInicial?: Date;
  dtValidadeFinal?: Date;
  useValidade?: boolean;
}

interface LicencaFilterReturnPayload {
  idLicenca: number | null;
  flExclusao?: string | null;
  idGabarito: number | null;
  comprimentoTotal?: number | null;
  transportador: TransportadorPayload;
  veiculoTratorPrincipal: VeiculoTratorPayload;
  carretasPrincipais?: Array<CarretaPayload>;
  rodizios?: Array<CarretaPayload>;
  engenheiro?: EngenheiroPayload;
  licencasOrgaos: Array<LicencaOrgaoPayload>;
  cliente?: ClientePayload | null;
  permissionInfo?: PermissionPayload;
}

interface LicencaPayload {
  idLicenca?: number | null;
  idLicencaOrigem?: number | null;
  flExclusao?: string | null;
  idGabarito?: number | null;
  excessoLateralEsquerdo?: number | null;
  excessoLateralDireito?: number | null;
  excessoDianteiro?: number | null;
  excessoTraseiro?: number | null;
  alturaTotal?: number | null;
  larguraTotal?: number | null;
  comprimentoTotal?: number | null;
  tipoCarga?: string | null;
  notaFiscalCarga?: DocumentoPayload | null;
  transportador: TransportadorPayload;
  engenheiro: EngenheiroPayload;
  licencasOrgaos?: Array<LicencaOrgaoPayload>;
  conjuntoTransportador: Array<VeiculoConjuntoTransportadorGabaritoPayload>;
  cliente?: ClientePayload | null;
  permissionInfo?: PermissionPayload;
}

type CategoriaHistorico = "CLIENTE" | "ROBO" | "MONITORAMENTO" | "SISTEMA";

type OrigemOperacao =
  | "USUARIO"
  | "API"
  | "ROBO_CADASTRO"
  | "ROBO_CONSULTA"
  | "ROBO_ART"
  | "ROBO_VEICULO"
  | "SCHEDULER"
  | "SCRIPT";

/** Linha da aba Histórico (GET /licenca/{id}/historico/full). */
interface HistoricoLicencaPayload {
  idHistoricoLicenca: number;
  idLicenca: number;
  idOrgao?: number | null;
  nomeOrgao?: string | null;
  operacao: string;
  descricao?: string | null;
  timestampOperacao?: Date | string | null;
  idUsuario?: number | null;
  nomeUsuario?: string | null;
  origem?: OrigemOperacao | null;
  categoria: CategoriaHistorico;
  campo?: string | null;
  valorAnterior?: string | null;
  valorNovo?: string | null;
  flPosProtocolo?: boolean | null;
  idLicencaOrigem?: number | null;
}

const CATEGORIAS_HISTORICO: Array<{
  categoria: CategoriaHistorico;
  descricao: string;
}> = [
  { categoria: "CLIENTE", descricao: "Cliente" },
  { categoria: "ROBO", descricao: "Robô" },
  { categoria: "MONITORAMENTO", descricao: "Monitoramento" },
  { categoria: "SISTEMA", descricao: "Sistema" }
];

const ORIGENS_OPERACAO: Record<OrigemOperacao, string> = {
  USUARIO: "Tela",
  API: "API",
  ROBO_CADASTRO: "Robô (cadastro)",
  ROBO_CONSULTA: "Robô (consulta)",
  ROBO_ART: "Robô (ART)",
  ROBO_VEICULO: "Robô (veículo)",
  SCHEDULER: "Rotina automática",
  SCRIPT: "Script"
};

interface LicencaPorVeiculoFilterPayload {
  idVeiculoTrator?: number | null;
  idCarretas: Array<number>;
  idCliente?: number | null;
}

const useLicencaService = () => {
  const {
    data,
    loading,
    get,
    post,
    put,
    patch,
    downloadToView,
    downloadFileNewTab,
    downloadFile
  } = useApiWithAuth();

  const searchLicenca = (
    filter: LicencaFilterPayload,
    page: number
  ): Promise<void> => {
    return get("/licenca", {
      ...clearObject(filter),
      ...{
        page: page
      }
    });
  };

  const printSearch = (
    filter: LicencaFilterPayload,
    xls: boolean,
    newTab?: boolean
  ): Promise<void> => {
    return (newTab ? downloadFileNewTab : downloadFile)(`/licenca/download`, {
      ...clearObject(filter),
      ...{
        xls: xls
      }
    });
  };

  const getLicenca = (idLicenca: number): Promise<void> => {
    return get(`/licenca/${idLicenca}`);
  };

  const newLicenca = (idCliente?: number | null): Promise<void> => {
    return get("/licenca/new", idCliente ? { idCliente: idCliente } : {});
  };

  const copyLicenca = (idLicenca: number): Promise<void> => {
    return get("/licenca/new", {
      idLicencaOrigem: idLicenca
    });
  };

  // categorias vazias = todas
  const getHistoricoLicenca = (
    idLicenca: number,
    categorias?: Array<CategoriaHistorico>
  ): Promise<void> => {
    return get(`/licenca/${idLicenca}/historico/full`, {
      categorias: categorias?.length ? categorias.join(",") : undefined
    });
  };

  const shelveLicenca = (idLicenca: number): Promise<void> => {
    return patch("/licenca/" + idLicenca + "/arquivar");
  };

  const unshelveLicenca = (idLicenca: number): Promise<void> => {
    return patch("/licenca/" + idLicenca + "/desarquivar");
  };

  const saveNewLicenca = (licenca: LicencaPayload): Promise<void> => {
    return post("/licenca", licenca);
  };

  const updateLicenca = (
    idLicenca: number,
    licenca: LicencaPayload
  ): Promise<void> => {
    return put(`/licenca/${idLicenca}`, licenca);
  };

  const getNomeLicencaVisualizacao = (entity?: LicencaFilterReturnPayload) => {
    if (!entity) {
      return "";
    }
    return (
      useVeiculoTratorService().getNomeVeiculoTrator(
        entity.veiculoTratorPrincipal
      ) +
      " - " +
      useTransportadorService().getNomeTransportador(entity.transportador) +
      " - " +
      useGabaritoService().getNomeGabarito(entity.idGabarito) +
      (entity.cliente
        ? " - " +
          (useAuth().amIAllowed("view", "root-cliente")
            ? useClienteService()
            : useClienteLogadoService()
          ).getNomeCliente(entity.cliente)
        : "")
    );
  };

  const downloadVisualizacao = (
    idLicenca: number,
    newTab?: boolean
  ): Promise<void | string> => {
    return (newTab ? downloadFileNewTab : downloadFile)(
      `/licenca/${idLicenca}/download/visualizacao`
    );
  };

  const downloadVisualizacaoExibicao = (
    idLicenca: number
  ): Promise<DownloadViewReturn> => {
    return downloadToView(`/licenca/${idLicenca}/download/visualizacao`);
  };

  const downloadNotaFiscalCarga = (
    idLicenca: number,
    newTab?: boolean
  ): Promise<void> => {
    return (newTab ? downloadFileNewTab : downloadFile)(
      `/licenca/${idLicenca}/nota-fiscal-carga`
    );
  };

  const getRelatorioLicencasPorVeiculo = (
    filter: LicencaPorVeiculoFilterPayload,
    page: number
  ): Promise<void> => {
    return get(`/licenca/combinacao-veiculos`, {
      ...clearObject(filter),
      ...{
        page: page
      }
    });
  };

  const safeLicenca = (entity?: LicencaPayload | null): LicencaPayload => {
    const ret = entity ?? {
      conjuntoTransportador: [],
      engenheiro: useEngenheiroService().safeEngenheiro(),
      transportador: useTransportadorService().safeTransportador()
    };
    ret.conjuntoTransportador = ret.conjuntoTransportador ?? [];
    ret.engenheiro = ret.engenheiro ?? useEngenheiroService().safeEngenheiro();
    ret.transportador =
      ret.transportador ?? useTransportadorService().safeTransportador();
    return ret;
  };

  const safeFilterLicenca = () => {
    return {
      flSituacoes: [],
      idCarretas: [],
      useValidade: false
    };
  };

  return {
    data,
    loading,
    searchLicenca,
    printSearch,
    shelveLicenca,
    unshelveLicenca,
    saveNewLicenca,
    copyLicenca,
    updateLicenca,
    newLicenca,
    getLicenca,
    getHistoricoLicenca,
    safeLicenca,
    safeFilterLicenca,
    getNomeLicencaVisualizacao,
    downloadVisualizacao,
    downloadVisualizacaoExibicao,
    downloadNotaFiscalCarga,
    getRelatorioLicencasPorVeiculo
  };
};

export {
  useLicencaService,
  LicencaFilterPayload,
  LicencaFilterReturnPayload,
  LicencaPayload,
  LicencaPorVeiculoFilterPayload,
  HistoricoLicencaPayload,
  CategoriaHistorico,
  OrigemOperacao,
  CATEGORIAS_HISTORICO,
  ORIGENS_OPERACAO
};
